import PropTypes from "prop-types";
import React from "react";

export const DOCUMENT_REFERENCE_DEREFERENCE = "DOCUMENT_REFERENCE_DEREFERENCE";

function pathForRef(ref) {
    const parts = [];
    while (ref) {
        parts.unshift(ref.id);
        ref = ref.parent;
    }
    return parts.join('/');
}

/**
 * I create a HOC that will automatically synchronize a DocumentReference's
 * data into a component prop. Consider this example:
 *
 * <pre>
   withDocumentReference({
     thumbnail: props => props.thumbnailRef,
   })(ViewArticle);
 * </pre>
 *
 * <p>This will return a component which delegate rendering to ViewArticle,
 * but will add a "thumbnail" prop populated with the document available at
 * "props.thumbnailRef".
 *
 * @param refs a map of refs the HOC should inject. Keys are the target prop
 *  and values are a function, accepting the current props, that return the
 *  DocumentReference to dereference and inject.
 * @return {Function} a HOC which will inject the configured refs' data into
 *  the passed component.
 */
function withDocumentReference(refs) {
    return function (WrappedComponent) {
        class WithDocumentReference extends React.PureComponent {

            constructor(props, context) {
                super(props, context);
                this.store = context.store;
                this.state = {};
                this.unsubs = [];
                for (let k in refs) {
                    if (refs.hasOwnProperty(k)) {
                        this.state[k] = null;
                    }
                }
            }

            componentWillMount() {
                for (let k in refs) {
                    if (refs.hasOwnProperty(k)) {
                        const r = refs[k](this.props);
                        if (r) {
                            this.bindRef(k, r);
                        }
                    }
                }
            }

            componentWillReceiveProps(nextProps) {
                this.unbindAll();
                for (let k in refs) {
                    if (refs.hasOwnProperty(k)) {
                        const curr = refs[k](this.props);
                        const next = refs[k](nextProps);
                        if (curr !== next && next) {
                            this.bindRef(k, next);
                        }
                    }
                }
            }

            componentWillUnmount() {
                this.unbindAll();
            }

            bindRef(key, m) {
                const state = this.store.getState();
                const path = pathForRef(m);
                this.setState({
                    [key]: state.docs[path],
                });
                this.unsubs.push(m.onSnapshot(s => {
                        const value = Object.assign({ id: s.id }, s.data());
                        this.store.dispatch({
                            type: DOCUMENT_REFERENCE_DEREFERENCE,
                            path,
                            value,
                        });
                        this.setState({
                            [key]: value,
                        });
                    },
                    (e) => console.warn("error", e)),
                );
            }

            unbindAll() {
                if (this.unsubs.length > 0) {
                    for (let i = 0, l = this.unsubs.length; i < l; i++) {
                        this.unsubs[i]();
                    }
                    this.unsubs = [];
                }
            }

            render() {
                return <WrappedComponent {...this.state} {...this.props} />;
            }
        }

        WithDocumentReference.contextTypes = Object.assign({
            store: PropTypes.object.isRequired,
        }, WrappedComponent.contextTypes || {});
        WithDocumentReference.propTypes = WrappedComponent.propTypes;

        return WithDocumentReference;
    };
}

export default withDocumentReference;



// WEBPACK FOOTER //
// ./src/util/withDocumentReference.js